/**
 * Guarded access to web storage for the analytics de-duplication modules.
 *
 * Storage can be absent or throw — Safari private browsing, a browser configured to block site
 * data, and the node environment the vitest config runs in all produce one of those. Analytics is
 * never worth an exception reaching the page, so every access is wrapped and a failure is reported
 * as "nothing stored" rather than propagating.
 *
 * `window` is read at call time rather than captured at module load, because it does not
 * necessarily exist when this module is first evaluated.
 */

export type AnalyticsStorageKind = 'localStorage' | 'sessionStorage';

/** The stored string, or null if absent, unavailable, or unreadable. */
export function readRaw(kind: AnalyticsStorageKind, key: string): string | null {
	try {
		return window[kind]?.getItem(key) ?? null;
	} catch {
		return null;
	}
}

/** Best-effort write. A failure is silent — the caller's in-memory guard is the fallback. */
export function writeRaw(kind: AnalyticsStorageKind, key: string, value: string): void {
	try {
		window[kind]?.setItem(key, value);
	} catch {
		// Storage unavailable; nothing useful to do here.
	}
}
